import React, { useState } from "react";
import FontIcon from "react-md/lib/FontIcons";
import moment from "moment";
import { ACTION_LABEL_MAP, APPOINTMENT_STATUS_LABEL_MAP } from "../../constants";
import { nameOf } from "./CallAudit";
import AppointmentLink from "./AppointmentLink";
import DocumentLink from "./DocumentLink";

const fmt = (d) => (d ? moment(d).format("D MMM YYYY, h:mm A") : "-");
const fmtTime = (d) => (d ? moment(d).format("h:mm A") : "");

export const appointmentStepsDone = (a) =>
  1 + (a.prescriptionId ? 1 : 0) + (a.actions && a.actions.length ? 1 : 0);
export const appointmentComplete = (a) => appointmentStepsDone(a) === 3;

function Row({ label, children }) {
  return (
    <div className="ltr-step-row">
      <span>{label}</span>
      <div>{children}</div>
    </div>
  );
}

function StepCard({ index, title, state, children, footnote }) {
  return (
    <div className={`ltr-step-card ${state}`}>
      <div className="ltr-step-card-head">
        <span className="ltr-step-num">
          {state === "done" ? <FontIcon>check</FontIcon> : state === "locked" ? <FontIcon>lock</FontIcon> : index}
        </span>
        <div className="ltr-step-card-title">
          <span className="ltr-step-kicker">Step {index}</span>
          <span>{title}</span>
        </div>
        <span className={`ltr-step-state ${state}`}>
          {state === "done" ? "Done" : state === "locked" ? "Locked" : "To do now"}
        </span>
      </div>
      <div className="ltr-step-card-body">{children}</div>
      {footnote && <div className="ltr-step-foot">{footnote}</div>}
    </div>
  );
}

function AppointmentSteps({ appt, caseRecord, names, isClosed, rxPending, onCreateRx, onCheckRx, onAssignAction }) {
  const hasRx = !!appt.prescriptionId;
  const hasActions = !!(appt.actions && appt.actions.length);
  const statusLabel = APPOINTMENT_STATUS_LABEL_MAP[appt.status] || "Scheduled";

  return (
    <div className="ltr-steps-row">
      <StepCard
        index={1}
        title="Create appointment"
        state="done"
        footnote={`Converted from call ${appt.callId || "-"} by ${nameOf(names, appt.createdBy)} · ${fmtTime(appt.createdAt)}`}
      >
        <Row label="Appointment ID">
          <AppointmentLink appointmentId={appt.appointmentId} />
        </Row>
        <Row label="Appointment status">
          <span className={`ltr-status-chip ${appt.status === 2 ? "done" : "pending"}`}>
            <FontIcon>{appt.status === 2 ? "check_circle" : "schedule"}</FontIcon>
            {statusLabel}
          </span>
        </Row>
        <Row label="Consultation">{fmt(appt.consultationAt)}</Row>
      </StepCard>

      <span className="ltr-step-arrow">
        <FontIcon>arrow_forward</FontIcon>
      </span>

      <StepCard
        index={2}
        title="Create Rx"
        state={hasRx ? "done" : "current"}
        footnote={
          hasRx ? `Issued by ${nameOf(names, appt.prescribedBy)} against appointment ${appt.appointmentId}` : null
        }
      >
        {hasRx ? (
          <>
            <Row label="Prescription">Rx-{appt.prescriptionId}</Row>
            <Row label="Document">
              <DocumentLink url={appt.prescriptionUrl} />
            </Row>
            <Row label="Issued">{fmt(appt.prescribedAt)}</Row>
          </>
        ) : isClosed ? (
          <p className="ltr-muted">No prescription was linked.</p>
        ) : (
          <div className="ltr-step-cta">
            {rxPending && (
              <p className="ltr-step-wait">
                Waiting for the Rx from the editor tab…{" "}
                <button type="button" className="ltr-text-btn" onClick={() => onCheckRx(appt)}>
                  Check now
                </button>
              </p>
            )}
            <button type="button" className="ltr-btn primary" onClick={() => onCreateRx(appt)}>
              <FontIcon>description</FontIcon>
              Create Rx
            </button>
          </div>
        )}
      </StepCard>

      <span className="ltr-step-arrow">
        <FontIcon>arrow_forward</FontIcon>
      </span>

      <StepCard
        index={3}
        title="Assign action"
        state={hasActions ? "done" : hasRx ? "current" : "locked"}
        footnote={hasActions ? `Set by ${nameOf(names, appt.actionsSetBy)} from the prescription` : null}
      >
        {hasActions ? (
          <>
            <Row label="Action">{appt.actions.map((a) => ACTION_LABEL_MAP[a]).join(", ")}</Row>
            {/* The case has one holder and one follow-up date, so only the
                newest appointment shows them; an older one's would be stale. */}
            {caseRecord && (
              <>
                <Row label="Assigned to">{caseRecord.assignedToName || "-"}</Row>
                <Row label="Follow up on">{fmt(caseRecord.followUpDate)}</Row>
              </>
            )}
            {appt.actionNote && <Row label="Closes when">{appt.actionNote}</Row>}
            <Row label="Set on">{fmt(appt.actionsSetAt)}</Row>
          </>
        ) : isClosed ? (
          <p className="ltr-muted">No action was set.</p>
        ) : (
          <div className="ltr-step-cta">
            <button
              type="button"
              className="ltr-btn primary"
              disabled={!hasRx}
              onClick={() => onAssignAction(appt)}
            >
              <FontIcon>{hasRx ? "flag" : "lock"}</FontIcon>
              Assign action
            </button>
          </div>
        )}
      </StepCard>
    </div>
  );
}

// The whole header expands the card, and the appointment id in it opens the
// appointment's details. A <button> cannot hold a link, so the header is a
// focusable element with button semantics instead.
function AppointmentHeader({ appt, expanded, onToggle, complete }) {
  const onKeyDown = (e) => {
    if (e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) return;
    e.preventDefault();
    onToggle();
  };
  return (
    <div
      role="button"
      tabIndex={0}
      className="ltr-appt-head"
      onClick={onToggle}
      onKeyDown={onKeyDown}
      aria-expanded={expanded}
    >
      <span className="ltr-appt-title">
        <FontIcon>{complete && !expanded ? "check_circle" : "event"}</FontIcon>
        <AppointmentLink appointmentId={appt.appointmentId}>Appointment {appt.appointmentId}</AppointmentLink>
        <span className="ltr-cell-sub">
          from call {appt.callId || "-"} · {fmt(appt.consultationAt || appt.createdAt)}
        </span>
      </span>
      <span className="ltr-appt-right">
        {complete && !expanded ? (
          <>
            <span className="ltr-cell-sub">
              Rx-{appt.prescriptionId} · {(appt.actions || []).map((a) => ACTION_LABEL_MAP[a]).join(", ")}
            </span>
            <span className="ltr-status-chip done">
              <FontIcon>check_circle</FontIcon>
              Complete
            </span>
          </>
        ) : (
          <span className={`ltr-soft-pill ${complete ? "done" : "pending"}`}>
            {appointmentStepsDone(appt)} of 3 done
          </span>
        )}
        <FontIcon>{expanded ? "expand_less" : "expand_more"}</FontIcon>
      </span>
    </div>
  );
}

/**
 * Required action: the work that belongs to each appointment, newest
 * appointment first. The order never changes as steps get done, so finishing
 * one appointment does not move the others: a finished appointment collapses
 * in place, and the newest one still needing work is open.
 */
function RequiredAction({ caseRecord, names, isClosed, rxPendingFor, onCreateRx, onCheckRx, onAssignAction }) {
  // Kept in the order they were created (the case appends each new one).
  const appointments = Array.isArray(caseRecord.appointments) ? caseRecord.appointments : [];
  const open = appointments.filter((a) => !appointmentComplete(a));
  const done = appointments.filter((a) => appointmentComplete(a));
  // The newest open appointment is the one to work; if all are complete, the
  // newest completed one stays expanded.
  const current = open.length ? open[open.length - 1] : null;
  const [expanded, setExpanded] = useState({});
  const isExpanded = (a) =>
    expanded[a.appointmentId] !== undefined
      ? expanded[a.appointmentId]
      : a === current || (!current && a === done[done.length - 1]);

  let pill;
  if (!appointments.length) pill = { cls: "idle", text: "No appointment yet" };
  else if (open.length)
    pill = {
      cls: "pending",
      text: `${appointments.length} appointment${appointments.length === 1 ? "" : "s"} · ${open.length} need${
        open.length === 1 ? "s" : ""
      } work`,
    };
  else if (appointments.length === 1) pill = { cls: "done", text: "3 of 3 done" };
  else pill = { cls: "done", text: `${appointments.length} appointments · all complete` };

  const latest = appointments[appointments.length - 1];
  const steps = (a) => (
    <AppointmentSteps
      appt={a}
      caseRecord={a === latest ? caseRecord : null}
      names={names}
      isClosed={isClosed}
      rxPending={rxPendingFor === a.appointmentId}
      onCreateRx={onCreateRx}
      onCheckRx={onCheckRx}
      onAssignAction={onAssignAction}
    />
  );

  const renderAppointment = (a, isCurrent) => {
    const complete = appointmentComplete(a);
    const exp = isExpanded(a);
    return (
      <div key={a.appointmentId} className={`ltr-appt${isCurrent ? " current" : ""}${complete ? " complete" : ""}`}>
        <AppointmentHeader
          appt={a}
          expanded={exp}
          complete={complete}
          onToggle={() => setExpanded((s) => ({ ...s, [a.appointmentId]: !exp }))}
        />
        {exp && <div className="ltr-appt-body">{steps(a)}</div>}
      </div>
    );
  };

  return (
    <section className="ltr-detail-card">
      <div className="ltr-card-title-row">
        <h3>Required action</h3>
        <span className={`ltr-soft-pill ${pill.cls}`}>{pill.text}</span>
      </div>
      {appointments.length > 0 && (
        <p className="ltr-card-sub ltr-card-lead">
          Required work belongs to each appointment. Convert a call into an appointment and that appointment must get a
          prescription and an action. Calls that never become appointments require nothing.
        </p>
      )}

      {!appointments.length ? (
        <div className="ltr-empty-box">
          <FontIcon>event_available</FontIcon>
          <strong>No appointment created yet</strong>
          <p>
            Nothing is required on this case until a call becomes an appointment. Use Create appointment on a call in
            the call audit above — the prescription and action steps appear only for that appointment.
          </p>
        </div>
      ) : (
        appointments
          .slice()
          .reverse()
          .map((a) => renderAppointment(a, a === current))
      )}
    </section>
  );
}

export default RequiredAction;
